<script>
export const BUTTON_SIZE = {
  small: 'small',
  medium: 'medium',
  large: 'large',
}

export const BUTTON_ALIGN = {
  center: 'center',
  left: 'left',
  right: 'right',
}

export const BUTTON_TYPE = {
  default: 'default',
  accent: 'default',
  primary: 'primary',
  secondary: 'secondary',
  tertiary: 'outline',
  quaternary: 'info',
  text: 'text',
}

export default {
  name: 'BaseButtonV2',
  props: {
    size: {
      type: String,
      default: 'medium',
      validator(value) {
        return Object.keys(BUTTON_SIZE).includes(value)
      },
    },
    align: {
      type: String,
      default: 'center',
      validator(value) {
        return Object.keys(BUTTON_ALIGN).includes(value)
      },
    },
    type: {
      type: String,
      default: 'default',
      validator(value) {
        return Object.keys(BUTTON_TYPE).includes(value)
      },
    },
    isWide: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    circle: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    buttonClass() {
      const defaultClass = 'is-lavender-design tamara-base-button'
      const dynamicClass = `is-button--${this.size} button-align--${this.align} is-button--${this.type}`
      const customClass = Object.entries({
        'is-wide': this.isWide,
        'is-loading': this.loading,
        'is-circle': this.circle,
      }).reduce((acc, [key, value]) => {
        if (value) {
          acc += ` ${key}`
        }

        return acc
      }, '')

      return [defaultClass, dynamicClass, customClass].filter(Boolean).join(' ')
    },
    buttonType() {
      return BUTTON_TYPE[this.type] || BUTTON_TYPE.default
    },
    attrs() {
      // The purpose of this is to remove the type attribute from the attrs object
      // so that it doesn't override the type attribute of the button
      const { type, loading, ...rest } = this.$attrs

      return {
        ...rest,
      }
    },
    disabled() {
      return this.loading || this.$attrs.disabled
    },
  },
}
</script>

<template>
  <el-button
    :class="buttonClass"
    :type="buttonType"
    :disabled="disabled"
    v-bind="attrs"
    v-on="$listeners"
  >
    <template v-if="loading">
      <div class="tamara-base-button__loading-icon">
        <svg-icon name="circle" width="20px" height="20px" />
      </div>
    </template>
    <span class="tamara-base-button__content-inner">
      <slot />
    </span>
  </el-button>
</template>

<style lang="scss" scoped>
@import '@/styles/lavender/design.scss';

.tamara-base-button {
  --tamara-base-button-height: 44px;
  --tamara-base-button-space-x: 16px;
  --tamara-base-button-space-y: 10px;
  --tamara-base-button-color-label: #fff;
  --tamara-base-button-color-bg: #9600f1;

  box-sizing: border-box;
  height: var(--tamara-base-button-height);
  padding: var(--tamara-base-button-space-y) var(--tamara-base-button-space-x);
  color: var(--tamara-base-button-color-label);
  background-color: var(--tamara-base-button-color-bg);
  border: 1px solid var(--tamara-color-functional-grey-light);
  border-radius: var(--tamara-border-radius-full);

  &:active,
  &:focus,
  &:hover {
    color: var(--tamara-base-button-color-label);
    background-color: var(--tamara-base-button-color-bg);
  }

  &.is-button--small {
    --tamara-base-button-height: 32px;
    --tamara-base-button-space-x: 12px;
    --tamara-base-button-space-y: 6px;

    @extend .tamara-english-medium-4;

    // Circle
    &.is-circle {
      width: 40px;
      height: 40px;
      padding: 12px;
    }

    &::v-deep {
      &.el-button--outline {
        --tamara-base-button-space-x: 10px;
        --tamara-base-button-space-y: 4px;
      }
    }
  }

  &.is-button--medium {
    --tamara-base-button-height: 44px;
    --tamara-base-button-space-x: 16px;
    --tamara-base-button-space-y: 10px;

    @extend .tamara-english-medium-3;

    &::v-deep {
      &.el-button--outline {
        --tamara-base-button-space-x: 14px;
        --tamara-base-button-space-y: 8px;
      }
    }
  }

  &.is-button--large {
    --tamara-base-button-height: 52px;
    --tamara-base-button-space-x: 24px;
    --tamara-base-button-space-y: 13px;

    @extend .tamara-english-medium-2;

    &::v-deep {
      &.el-button--outline {
        --tamara-base-button-space-x: 22px;
        --tamara-base-button-space-y: 11px;
      }
    }
  }

  &.button-align--left {
    --tamara-button-align-content: flex-start;
  }

  &.button-align--right {
    --tamara-button-align-content: flex-end;
  }

  &.button-align--center {
    --tamara-button-align-content: center;
  }

  &.is-wide {
    width: 100%;
  }

  &.is-loading {
    .tamara-base-button {
      &__loading-icon {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      &__content-inner {
        visibility: hidden;
      }
    }
  }

  &::v-deep {
    > span {
      display: flex;
      align-items: center;
      justify-content: var(--tamara-button-align-content);
    }

    &:active,
    &:focus {
      --tamara-button-focus-shadow-color: #dea5ff66;

      box-shadow: 0 0 0 3px var(--tamara-button-focus-shadow-color);
    }

    // Default or Accent
    &.el-button--default {
      --tamara-base-button-color-label: var(--tamara-color-content-inverted);
      --tamara-base-button-color-bg: var(--tamara-color-brand-700);

      border: none;

      &:active,
      &:focus,
      &:hover {
        --tamara-base-button-color-bg: var(--tamara-color-brand-800);

        border: none;
      }
    }

    // Primary
    &.el-button--primary {
      --tamara-base-button-color-label: var(--tamara-color-content-inverted);
      --tamara-base-button-color-bg: var(--tamara-color-consumer-brand-700);

      border: none;

      &:active,
      &:focus,
      &:hover {
        --tamara-base-button-color-bg: var(--tamara-color-consumer-brand-800);
        --tamara-button-focus-shadow-color: var(
          --tamara-color-background-neutral-mid
        );

        border: none;
      }
    }

    // Secondary
    &.el-button--secondary {
      --tamara-base-button-color-label: var(--tamara-color-content-high);
      --tamara-base-button-color-bg: var(--tamara-color-background-neutral-mid);

      border: none;

      &:active,
      &:focus,
      &:hover {
        border: none;
        border-color: var(--tamara-color-background-neutral-high);

        --tamara-button-focus-shadow-color: var(
          --tamara-color-background-neutral-low
        );
      }
    }

    // Tertiary
    &.el-button--outline {
      --tamara-base-button-color-label: var(--tamara-color-content-high);

      background-color: var(--tamara-color-background-bright);
      border: 2px solid var(--tamara-color-border-mid);

      &:active,
      &:focus,
      &:hover {
        --tamara-base-button-color-label: var(--tamara-color-content-high);
        --tamara-button-focus-shadow-color: var(
          --tamara-color-background-neutral-low
        );

        background-color: var(--tamara-color-background-neutral-low);
      }
    }

    // Quaternary
    &.el-button--info {
      --tamara-base-button-color-label: var(--tamara-color-content-high);

      background-color: transparent;
      border: none;

      &:active,
      &:focus,
      &:hover {
        --tamara-button-focus-shadow-color: var(
          --tamara-color-background-neutral-low
        );

        background-color: var(--tamara-color-background-neutral-low);
      }
    }

    // Text
    &.el-button--text {
      background-color: transparent;
      border: none;

      &:active,
      &:focus,
      &:hover {
        background-color: transparent;
        box-shadow: none;
      }
    }

    &.is-disabled {
      --tamara-base-button-color-label: var(--tamara-color-content-low);
      --tamara-base-button-color-bg: var(--tamara-color-background-neutral-low);

      background-color: var(--tamara-color-background-neutral-low) !important;
      border: none;
      box-shadow: none;

      &:active,
      &:focus,
      &:hover {
        --tamara-base-button-color-label: var(--tamara-color-content-low);
        --tamara-base-button-color-bg: var(
          --tamara-color-background-neutral-low
        );

        cursor: not-allowed;
        border: none;
        box-shadow: none;
      }
    }
  }
}
</style>
